/**
 * WorkbookViewer — a spreadsheet view over CSV/XLSX bytes (or an already-parsed document).
 *
 * Read-only by default, but the presentation interactions all work on a local working copy:
 * autofit / resize columns, sort, pin, per-column filters, find. Pass `editable` to allow cell
 * edits (with undo/redo) and receive `onDocChange` callbacks — the importer builds on this.
 */
import { DownloadIcon, FileSpreadsheetIcon, Redo2Icon, SheetIcon, Undo2Icon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { docFilename, docToXlsxBlob, parseSpreadsheet, sheetToCsvBlob, triggerBlobDownload } from './sheet-io';
import { SheetGrid, type ColumnBand, type SelectionInfo, type SheetDecorations, type SheetGridHandle } from './SheetGrid';
import type { SheetData, SheetDoc } from './sheet-model';

export interface WorkbookViewerProps {
  /** Either raw bytes + filename … */
  filename?: string;
  bytes?: ArrayBuffer;
  /** … or a ready document (controlled). */
  doc?: SheetDoc;
  onDocChange?: (doc: SheetDoc) => void;
  editable?: boolean;
  /** Called when the active sheet changes (index). */
  onActiveSheetChange?: (index: number) => void;
  decorations?: SheetDecorations;
  columnBand?: ColumnBand;
  rowPredicate?: (dataRow: number) => boolean;
  onSelectionInfo?: (info: SelectionInfo) => void;
  /** Extra controls rendered in the toolbar (right side). */
  toolbar?: ReactNode;
  /** Status text under the grid (left). */
  status?: ReactNode;
  showDownload?: boolean;
  className?: string;
  gridRef?: React.Ref<SheetGridHandle>;
}

const MAX_UNDO = 100;

export function WorkbookViewer({ filename, bytes, doc: controlledDoc, onDocChange, editable = false, onActiveSheetChange, decorations, columnBand, rowPredicate, onSelectionInfo, toolbar, status, showDownload = true, className, gridRef }: WorkbookViewerProps) {
  const parsed = useMemo(() => {
    if (controlledDoc) return { doc: controlledDoc, error: null as string | null, truncated: [] as { sheet: string; keptRows: number }[] };
    if (!filename || !bytes) return { doc: null, error: null, truncated: [] };
    try {
      const r = parseSpreadsheet(filename, bytes);
      return { doc: r.doc, error: null, truncated: r.truncated };
    } catch (e) {
      return { doc: null, error: e instanceof Error ? e.message : 'Could not read this spreadsheet.', truncated: [] };
    }
  }, [controlledDoc, filename, bytes]);

  // Local working copy: view interactions (and edits when editable) apply here.
  const [doc, setDoc] = useState<SheetDoc | null>(parsed.doc);
  useEffect(() => setDoc(parsed.doc), [parsed.doc]);
  const undoRef = useRef<SheetData['rows'][]>([]);
  const redoRef = useRef<SheetData['rows'][]>([]);
  const [, bump] = useState(0);
  const internalGridRef = useRef<SheetGridHandle | null>(null);

  const activeIndex = doc ? Math.min(doc.activeSheet, doc.sheets.length - 1) : 0;
  const sheet = doc?.sheets[activeIndex] ?? null;

  const emit = useCallback(
    (next: SheetDoc) => {
      setDoc(next);
      onDocChange?.(next);
    },
    [onDocChange],
  );

  const apply = useCallback(
    (fn: (s: SheetData) => SheetData, opts: { undoable?: boolean } = {}) => {
      setDoc((d) => {
        if (!d) return d;
        const idx = Math.min(d.activeSheet, d.sheets.length - 1);
        const cur = d.sheets[idx];
        if (!cur) return d;
        const next = fn(cur);
        if (next === cur) return d;
        const structural = next.rows !== cur.rows;
        if (structural && opts.undoable !== false) {
          undoRef.current.push(cur.rows);
          if (undoRef.current.length > MAX_UNDO) undoRef.current.shift();
          redoRef.current = [];
        }
        const sheets = d.sheets.slice();
        sheets[idx] = next;
        const out = { ...d, sheets, dirty: d.dirty || structural };
        if (structural) queueMicrotask(() => onDocChange?.(out));
        return out;
      });
    },
    [onDocChange],
  );

  const undo = useCallback(() => {
    const prev = undoRef.current.pop();
    if (!prev || !doc || !sheet) return;
    redoRef.current.push(sheet.rows);
    const sheets = doc.sheets.slice();
    sheets[activeIndex] = { ...sheet, rows: prev };
    emit({ ...doc, sheets, dirty: true });
    bump((n) => n + 1);
  }, [doc, sheet, activeIndex, emit]);

  const redo = useCallback(() => {
    const next = redoRef.current.pop();
    if (!next || !doc || !sheet) return;
    undoRef.current.push(sheet.rows);
    const sheets = doc.sheets.slice();
    sheets[activeIndex] = { ...sheet, rows: next };
    emit({ ...doc, sheets, dirty: true });
    bump((n) => n + 1);
  }, [doc, sheet, activeIndex, emit]);

  const setActive = (i: number) => {
    if (!doc) return;
    undoRef.current = [];
    redoRef.current = [];
    emit({ ...doc, activeSheet: i });
    onActiveSheetChange?.(i);
  };

  if (parsed.error) return <div className="flex h-64 items-center justify-center p-6 text-sm text-muted-foreground">{parsed.error}</div>;
  if (!doc || !sheet) return <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  const dataRows = Math.max(0, sheet.rows.length - (sheet.frozenHeader ? 1 : 0));
  const truncated = parsed.truncated.find((t) => t.sheet === sheet.name);

  return (
    <div className={cn('flex h-full min-h-[320px] flex-col border bg-card', className)}>
      <div className="flex items-center gap-2 border-b px-2 py-1.5">
        <FileSpreadsheetIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-xs font-medium" title={doc.name}>
          {doc.name}
        </span>
        <span className="text-muted-foreground text-[11px] tabular-nums">
          {dataRows.toLocaleString()} rows · {sheet.rows.reduce((m, r) => Math.max(m, r.length), 0)} columns
        </span>
        {truncated && <span className="text-warning text-[11px]">Showing the first {truncated.keptRows.toLocaleString()} rows</span>}
        <div className="ml-auto flex items-center gap-1">
          {editable && (
            <>
              <Button variant="ghost" size="icon-sm" onClick={undo} disabled={undoRef.current.length === 0} aria-label="Undo" title="Undo (Ctrl+Z)">
                <Undo2Icon />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={redo} disabled={redoRef.current.length === 0} aria-label="Redo" title="Redo (Ctrl+Y)">
                <Redo2Icon />
              </Button>
            </>
          )}
          {toolbar}
          {showDownload && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (doc.kind === 'csv' || doc.sheets.length === 1 ? triggerBlobDownload(sheetToCsvBlob(sheet), docFilename(doc, 'csv', sheet)) : triggerBlobDownload(docToXlsxBlob(doc), docFilename(doc, 'xlsx')))}
              title="Download the current view"
            >
              <DownloadIcon /> Download
            </Button>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <SheetGrid
          key={`${doc.id}:${sheet.id}`}
          ref={(h) => {
            internalGridRef.current = h;
            if (typeof gridRef === 'function') gridRef(h);
            else if (gridRef && 'current' in gridRef) (gridRef as React.MutableRefObject<SheetGridHandle | null>).current = h;
          }}
          sheet={sheet}
          apply={apply}
          onUndo={undo}
          onRedo={redo}
          readOnly={!editable}
          decorations={decorations}
          columnBand={columnBand}
          rowPredicate={rowPredicate}
          onSelectionInfo={onSelectionInfo}
        />
      </div>
      <div className="flex items-center gap-2 border-t bg-muted/30 px-2 py-1 text-[11px] text-muted-foreground">
        {doc.sheets.length > 1 ? (
          <div className="flex items-center gap-0.5 overflow-x-auto">
            {doc.sheets.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(i)}
                className={cn('flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium transition-colors', i === activeIndex ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
              >
                <SheetIcon className="size-3 shrink-0" />
                <span className="max-w-[140px] truncate">{s.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <span className="flex items-center gap-1 px-1">
            <SheetIcon className="size-3" /> {sheet.name}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">{status}</div>
      </div>
    </div>
  );
}
